import { useEffect, useState } from "react";

// Cosmetic typewriter reveal for a piece of text. Used to make a PERSISTED
// message (e.g. the onboarding opener, loaded from the DB) appear to stream in
// like a live answer, without involving the chat transport. `enabled === false`
// returns the full text immediately (done). The effect lives in this hook, not
// in a business component (webapp rule), and re-runs only when the text, the
// speed or the enabled flag changes — so it reveals once on mount and never
// replays.

// A message and a headline need opposite cadences: an answer must keep up with
// the reader (batched characters, so a long paragraph does not outstay its
// welcome), a one-line headline must be slow enough to READ as typing — batched
// at answer speed it would be over before the eye lands on it.
export const TYPEWRITER_SPEED = {
  answer: { charsPerTick: 3, tickMs: 24 },
  headline: { charsPerTick: 1, tickMs: 28 },
  // A footnote is not read as it types: it only has to arrive, quickly.
  notice: { charsPerTick: 2, tickMs: 16 },
} as const satisfies Record<string, { charsPerTick: number; tickMs: number }>;

// `delayMs` holds the first character back, so the text can follow the
// entrance of what sits above it.
export function useTypewriter(
  fullText: string,
  enabled: boolean,
  speed: (typeof TYPEWRITER_SPEED)[keyof typeof TYPEWRITER_SPEED],
  delayMs: number
): { text: string; done: boolean } {
  const [revealed, setRevealed] = useState(0);
  const { charsPerTick, tickMs } = speed;

  useEffect(() => {
    if (!enabled || fullText === "") return;
    setRevealed(0);
    let interval: ReturnType<typeof setInterval> | null = null;
    const start = setTimeout(() => {
      interval = setInterval(() => {
        setRevealed((current) => {
          const next = current + charsPerTick;
          if (next >= fullText.length) {
            if (interval !== null) clearInterval(interval);
            return fullText.length;
          }
          return next;
        });
      }, tickMs);
    }, delayMs);
    return () => {
      clearTimeout(start);
      if (interval !== null) clearInterval(interval);
    };
  }, [fullText, enabled, charsPerTick, tickMs, delayMs]);

  if (!enabled) return { text: fullText, done: true };
  return {
    text: fullText.slice(0, revealed),
    done: revealed >= fullText.length,
  };
}
